import { Button, Col, Form, Input, Row, Select, Switch } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { CreateDistrictPayload, onGetDistrict, onUpdateDistrict } from 'modules/districts/services/districts';
import { onGetAllCities } from 'modules/cities/services/cities';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	city_id: number;
	active: 0 | 1;
};

function UpdateDistrictPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [cities, setCities] = useState<{ label: string; value: number }[]>([]);
	const [updateForm] = Form.useForm();

	useEffect(() => {
		getDistrict();
	}, []);

	const getDistrict = async () => {
		try {
			const districtResponse = await onGetDistrict(id as string);
			const district = districtResponse.data.data;

			const citiesResponse = await onGetAllCities();
			setCities(
				citiesResponse.data.data.map((el: any) => {
					return { label: el.name, value: el.id };
				}),
			);

			updateForm.setFieldsValue({ 'name[ar]': district.name.ar });
			updateForm.setFieldsValue({ 'name[en]': district.name.en });
			updateForm.setFieldsValue({ city_id: district.city.id });
			updateForm.setFieldsValue({ active: district.active });

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('DISTRICTS.FETCH_ERROR'));
			navigate('/districts');
		}
	};

	const onStatusChange = async (checked: boolean) => {
		updateForm.setFieldValue('active', checked ? 1 : 0);
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateDistrictPayload: Partial<CreateDistrictPayload> = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				active: values.active,
				city_id: values.city_id,
			};

			await onUpdateDistrict(id as string, updateDistrictPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('DISTRICTS.UPDATED_SUCCESS'));
			navigate('/districts');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('DISTRICTS.FETCH_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('DISTRICTS.UPDATE_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer">
					<h5 style={{ marginBottom: 20 }}>{t('DISTRICTS.DETAILS')}</h5>

					<Form name="updateForm" form={updateForm} onFinish={onSubmit}>
						<h6>{t('DISTRICTS.INFORMATION')}</h6>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('DISTRICTS.VALIDATION.NAME_EN') }]}>
									<Input size="large" placeholder={t('DISTRICTS.PLACEHOLDER.NAME_EN')} />
								</Form.Item>
							</Col>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('DISTRICTS.VALIDATION.NAME_AR') }]}>
									<Input size="large" placeholder={t('DISTRICTS.PLACEHOLDER.NAME_AR')} />
								</Form.Item>
							</Col>
						</Row>

						<h6>{t('DISTRICTS.CITY')}</h6>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="city_id" rules={[{ required: true, message: t('DISTRICTS.VALIDATION.CITY') }]}>
									<Select
										style={{ width: '100%' }}
										size="large"
										options={cities}
										loading={isLoading}
										showSearch
										filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row>
							<Col span={24} md={12}>
								<div className="d-flex gap-2 mb-3">
									<Form.Item<FieldType> name="active" hidden>
										<Input />
									</Form.Item>
									<Switch defaultChecked={updateForm.getFieldValue('active')} onChange={onStatusChange} />
									<p className="mb-0 fw-medium">{t('COMMON.STATUS')}</p>
								</div>
							</Col>
						</Row>
					</Form>

					<Form.Item>
						<Button
							size="middle"
							type="primary"
							htmlType="submit"
							form="updateForm"
							loading={isSubmitting}
							style={{ marginTop: 20, display: 'flex' }}>
							{t('COMMON.UPDATE')}
						</Button>
					</Form.Item>
				</div>
			)}
		</>
	);
}

export default UpdateDistrictPage;
